<template>
  <el-drawer
    v-model="visible"
    :modal="false"
    :append-to-body="false"
    :fullscreen="false"
    :size="drawerWidthPct"
    destroy-on-close
    class="task-detail-drawer"
    modal-class="task-detail-drawer"
    @opened="onOpened"
    @closed="onClosed"
  >
    <template #header>
      <div v-if="isDataLoaded" class="task-detail-drawer__header">
        <template v-if="isAccessible">
          <template v-if="!$screen.isMobile($mq)">
            <avatar :person="details.assignee" :size="24" />
            <priority-chicklet :priority="details.priority" size="medium" />
            <status-chicklet :status="details.status" size="medium" max-width="130px" />
            <due-date-chicklet :due-date="details.dueDate" size="medium" />
          </template>
          <linked-item-drawer-dropdown
            v-if="originatingItem && $permissions.isNotGuest"
            :label="unlinkLabel"
            @unlink-item-click="$emit('unlink-task')"
          />
        </template>
        <prev-next-arrows
          v-if="prevItemForNav || nextItemForNav"
          :previous-item="prevItemForNav"
          :next-item="nextItemForNav"
          @navigate="(item) => $emit('navigate', item)"
        />
      </div>
    </template>
    <template v-if="isDataLoaded">
      <template v-if="isAccessible">
        <div class="task-detail-drawer__breadcrumb">
          <span>{{details.project.name}}</span>
          &nbsp;>&nbsp;
          <span>#{{details.refNumber}}</span>
        </div>
        <div class="task-detail-drawer__title">{{details.title}}</div>
        <!--MOBILE META DATA -->
        <div v-if="$screen.isMobile($mq)" class="task-detail-drawer__meta-mobile">
          <avatar :person="details.assignee" :size="24" />
          <priority-chicklet :priority="details.priority" size="medium" />
          <status-chicklet :status="details.status" size="medium" max-width="130px" />
          <due-date-chicklet :due-date="details.dueDate" size="medium" />
        </div>
        <div class="task-detail-drawer__description">
          <div class="user-inputed-rich-text" v-html="details.description" />
        </div>
        <div class="task-detail-drawer__history">
          <div
            v-for="(entry, index) in history"
            :key="index"
            class="task-detail-drawer__history-entry"
          >
            <div class="task-detail-drawer__history-entry__action">
              <span v-html="entry.createdOn" />
              <span v-html="entry.action" />
            </div>
            <div
              v-if="index !== 0 && entry.description"
              class="task-detail-drawer__history-entry__description user-inputed-rich-text"
              v-html="entry.description"
            />
            <!-- ATTACHMENTS LIST -->
            <attachments-list :attachments="entry.attachments"  />
          </div>
        </div>
        <div class="task-detail-drawer__footer">
          <dd-button
            :color="ColorAssistant.ProjectColor"
            full-width 
            size="large" 
            @click.exact="goToTask()" 
            @click.meta="goToTask(true)"
          >View full task</dd-button>
        </div>
      </template>
      <!-- No access -->
      <div v-else class="task-detail-drawer__no-access">
        <h2 v-if="requestedAccessToProject">Access requested!</h2>
        <h2 v-else>Request access to this task</h2>
        <div class="task-detail-drawer__breadcrumb">
          <span>{{task.entity.name}}</span>
          &nbsp;>&nbsp;
          <span>#{{task.refNumber}}</span>
        </div>
        <p v-if="requestedAccessToProject">{{ projectRequestMessage }}</p>
        <template v-else>
          <p>
            You don&rsquo;t have access to this task&rsquo;s project:
            <strong>{{task.entity.name}}</strong>.
            Click the button below and we&rsquo;ll send an email to a project administrator.
            Once approved, you can view this task.
          </p>
          <dd-button
            :is-loading="isRequestingAccess"
            color="brownish-orange"
            size="large"
            @click="requestAccess()"
          >Request Access +</dd-button>
        </template>
      </div>
    </template>
    <loading-placeholder v-else :lines="20" />
  </el-drawer>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import { uniq } from 'lodash-es'

/* Mixins */

/* Components */
import LinkedItemDrawerDropdown from '@/components/item/linked-item-drawer-dropdown.vue'

/* Helpers */
import TextAssistant from '@/helpers/text-assistant'
import RouteAssistant from '@/helpers/route-assistant'
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import { ILabelValuePair } from '@/types/data-objects/global'
import { IBreadcrumbItemMenuItem } from '@/types/data-objects/items'
import { IAttachment } from '@/types/data-objects/attachments'
import {
  IItemReference,
  ILinkedItemForListing,
  ITasksOutputDetail,
  ITasksOutputHistory,
} from '@/api/types/response.types'
import { EEntities } from '@/types'

/* API */
import TasksAPI from '@/api/modules/tasks.api'
import InternalProjectsAPI from '@/api/modules/internal-projects.api'

export default defineComponent({
  name: 'TaskDetailDrawer',

  components: {
    LinkedItemDrawerDropdown,
  },

  props: {
    showDrawer: {
      type: Boolean,
      default: false,
    },
    originatingItem: {
      type: Object as () => IItemReference,
      required: false,
      default: null,
    },
    task: {
      type: Object as () => ILinkedItemForListing,
      required: true,
    },
    prevItem: {
      type: Object as () => ILinkedItemForListing,
      required: false,
      default: null,
    },
    nextItem: {
      type: Object as () => ILinkedItemForListing,
      required: false,
      default: null,
    },
  },

  emits: ['navigate', 'unlink-task', 'update:showDrawer'],

  data() {
    return {
      EEntities,
      ColorAssistant,
      drawerOpened: false,
      isDataLoaded: false,
      isAccessible: false,
      isRequestingAccess: false,
      projectsRequestedAccessTo: [] as ILabelValuePair[],
      details: {} as ITasksOutputDetail,
      globalAttachments: [] as IAttachment[],
      history: [] as ITasksOutputHistory[],
    }
  },

  computed: {
    visible: {
      get(): boolean {
        return this.showDrawer
      },
      set(newValue: boolean) {
        this.$emit('update:showDrawer', newValue)
      },
    },
    drawerWidthPct(): string {
      if (this.$screen.isMobile(this.$mq)) {
        return '100%'
      }
      return '524px'
    },
    prevItemForNav(): IBreadcrumbItemMenuItem {
      if (!this.prevItem) {
        return null
      }
      return {
        itemID: this.prevItem.id,
        entityID: this.prevItem.entity.id,
        entityName: this.prevItem.entity.name,
        entityType: TextAssistant.entitySingularName(EEntities.PROJECTS),
        refNumber: this.prevItem.refNumber,
        title: this.prevItem.title,
      }
    },
    nextItemForNav(): IBreadcrumbItemMenuItem {
      if (!this.nextItem) {
        return null
      }
      return {
        itemID: this.nextItem.id,
        entityID: this.nextItem.entity.id,
        entityName: this.nextItem.entity.name,
        entityType: TextAssistant.entitySingularName(EEntities.PROJECTS),
        refNumber: this.nextItem.refNumber,
        title: this.nextItem.title,
      }
    },
    requestedAccessToProject(): boolean {
      return this.projectsRequestedAccessTo.some(p => {
        return p.value === this.task.entity.id
      })
    },
    projectRequestMessage(): string {
      return this.projectsRequestedAccessTo.find(p => {
        return p.value === this.task.entity.id
      }).label
    },
    unlinkLabel(): string {
      return 'Unlink from #' + this.originatingItem.refNumber
    },
  },

  watch: {
    task: {
      /* Subsequent data fetches (prev/next arrows). Check for drawerOpened to avoid
         double fetching on initial task load */
      async handler(value) {
        if (this.drawerOpened) {
          await this.fetchData()
        }
      },
    },
    drawerOpened: {
      /* Initial data fetch should happen only after drawer finishes opening */
      async handler(value) {
        if (value) {
          await this.fetchData()
        }
      },
    },
  },

  methods: {
    async onOpened() {
      this.drawerOpened = true
    },

    onClosed() {
      this.drawerOpened = false
      this.isDataLoaded = false
      this.clearData()
    },

    clearData() {
      this.details = {} as ITasksOutputDetail
      this.globalAttachments = [] as IAttachment[]
      this.history = [] as ITasksOutputHistory[]
    },

    async fetchData() {
      this.isDataLoaded = false
      try {
        await Promise.all([this.fetchHistory(), this.fetchTaskData()])
        this.isAccessible = true
      } catch (error) {
        if (error.status === 403) {
          this.isAccessible = false
        } else {
          this.$error.emit(error, true)
        }
      } finally {
        this.isDataLoaded = true
      }
    },

    async fetchHistory() {
      const response = await TasksAPI.get_History(
        this.task.entity.id,
        this.task.id,
        'chron',
      )

      const allAttachments: IAttachment[] = []
      response.forEach(item => {
        allAttachments.push(...item.attachments)
      })
      this.globalAttachments = uniq(allAttachments)
      this.history = response
    },

    async fetchTaskData() {
      this.details = await TasksAPI.get_Detail(
        this.task.entity.id,
        this.task.id,
      )
    },

    async requestAccess() {
      try {
        this.isRequestingAccess = true
        const response = await InternalProjectsAPI.put_RequestAccess(
          this.task.entity.id,
        )
        const projectAccessRequested = {
          label: response.message,
          value: this.task.entity.id,
        }
        this.projectsRequestedAccessTo.push(projectAccessRequested)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isRequestingAccess = false
      }
    },

    goToTask(newWindow?: boolean) {
      RouteAssistant.goToTask(
        this.$router,
        this.$route.params.accountId,
        this.task.entity.id,
        this.task.id,
        this.$permissions,
        this.task.entity.name,
        this.task.refNumber,
        this.task.title,
        newWindow,
      )
    },
  },
})
</script>

<style lang="scss">
.task-detail-drawer {
  &__header {
    display: flex;
    align-items: center;

    > div {
      margin-right: 8px;
    }

    > div.lidd {
      margin-left: 4px;
    }

    > div:last-child {
      margin-left: auto;
    }

    @include media-breakpoint-down(sm) {
      padding-left: 10px;
    }
  }

  &__meta-mobile {
    display: flex;
    align-items: center;

    > div {
      margin-right: 8px;
    }

    > .avatar {
      margin-left: 0;
    }

    padding-bottom: 24px;
  }

  &__breadcrumb {
    padding-bottom: 16px;
    @include lettering--p-sm;
    color: var(--color-text-third);
  }

  &__title {
    padding-bottom: 20px;
    @include lettering--h2;
  }

  &__description {
    margin-bottom: $detail-description-bottom-buffer;
  }

  &__history {
    padding-top: 10px;
    height: 100%;
    width: calc(100% + 60px);
    left: -30px;
    position: relative;
    background-color: var(--color-background-off-white);
    border-top: $border--solid-light;

    &-entry {
      padding: 20px 30px;
      background-color: var(--color-background-off-white);
      border-bottom: 1px dotted $color--gray-30;

      &:last-child {
        padding-bottom: 100px;
      }

      &__action {
        display: flex;
        flex-direction: column;
        padding-bottom: 20px;

        /* Created on */
        > span:first-child {
          padding-bottom: 2px;
          font-size: 12px;
          color: $slate;
        }

        /* Action text */
        > span:last-child > p {
          line-height: 20px;
        }
      }

      &__description {
        margin-bottom: $detail-description-bottom-buffer;
      }
    }
  }

  &__footer {
    display: flex;
    justify-content: center;
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    padding: 20px 30px;
    background-color: var(--color-general-white);
    border-top: solid 1px $silver-two;
  }

  &__no-access {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    > button {
      margin-top: 20px;
      width: 100%;
    }
  }
}
</style>
